/**
 * Central registration validation rules (shared between web-client and mobile).
 * When editing, apply the same changes to both registrationFieldRules.ts copies.
 * Aligns with backend/src/utils/passwordValidator.js (password rules).
 */

import { US_STATES } from '../constants/usStates';

export const FIELD_LIMITS = {
  fullName: 100,
  email: 150,
  street: 150,
  unit: 30,
  city: 80,
  zipDisplay: 10,
  salonName: 120,
} as const;

const VALID_STATE_CODES = new Set(US_STATES.map((s) => s.value));

/** Matches backend passwordValidator special character set (subset used in regex). */
const PASSWORD_SPECIAL_RE = /[!@#$%^&*()_+\-=[\]{};'":,.<>?/|`~]/;

export const R = {
  fullNameRequired: 'Please enter your full name.',
  fullNameInvalid: 'Please enter a valid name.',
  emailRequired: 'Please enter your email address.',
  emailInvalid: 'Please enter a valid email address.',
  travelDistanceRequired: 'Please enter how far you can travel (miles).',
  travelDistanceMax: 'Travel distance cannot exceed 100 miles.',
  unitTooLong: 'Please shorten your unit or suite.',
  passwordRequired: 'Please create a password.',
  passwordRules:
    'Password must be at least 8 characters and include upper, lower, a number, and a special character.',
  confirmRequired: 'Please confirm your password.',
  confirmMismatch: 'Passwords do not match.',
  phoneInvalid: 'Please enter a valid phone number.',
  streetRequired: 'Please enter your street address.',
  streetInvalid: 'Please remove unsupported characters or emojis.',
  cityRequired: 'Please enter a valid city.',
  cityInvalid: 'Please enter a valid city.',
  stateRequired: 'Please select a state.',
  zipInvalid: 'Please enter a valid ZIP code.',
  salonRequired: 'Please enter your salon name.',
  salonInvalid: 'Please remove unsupported characters or emojis.',
  specialtyRequired: 'Please select at least one specialty.',
} as const;

const EMAIL_RE = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

export function hasEmoji(text: string): boolean {
  if (!text) return false;
  try {
    return /\p{Extended_Pictographic}/u.test(text);
  } catch {
    return /[\u{1F300}-\u{1F9FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}]/u.test(text);
  }
}

/** Letters / marks, digits, spaces, periods, apostrophes, hyphens */
const NAME_CHARS_RE = /^[\p{L}\p{M}0-9\s'.-]+$/u;

/** Salon: letters, marks, digits, spaces, &, periods, apostrophes, hyphens */
const SALON_CHARS_RE = /^[\p{L}\p{M}0-9\s&'.-]+$/u;

/** City: letters, marks, spaces, periods, apostrophes, hyphens */
const CITY_CHARS_RE = /^[\p{L}\p{M}\s'.-]+$/u;

/** Street: letters, marks, digits, spaces, commas, periods, hyphens, #, / */
const STREET_CHARS_RE = /^[\p{L}\p{M}0-9\s,.#'\-/]+$/u;

const REPEATED_DIGITS = /^(\d)\1{9}$/;
const KNOWN_FAKE_PHONES = new Set(['1234567890', '0123456789', '0987654321']);

/** N × 11 service-style codes are not valid NPAs or central office codes (211 … 911). */
function isNanpN11Reserved(nxx: string): boolean {
  return nxx.length === 3 && nxx[1] === '1' && nxx[2] === '1';
}

/**
 * NANP area code (NPA): leading digit 2–9; not N11; not reserved/unassigned blocks.
 */
function isValidNanpAreaCode(npa: string): boolean {
  if (npa.length !== 3) return false;
  const d0 = npa[0]!;
  if (d0 < '2' || d0 > '9') return false;
  if (isNanpN11Reserved(npa)) return false;
  if (npa === '555' || npa === '666') return false;
  if (npa.startsWith('37') || npa.startsWith('96')) return false;
  return true;
}

/**
 * Central office code (exchange): leading digit 2–9; not N11; not 555/000/911.
 */
function isValidNanpExchange(nxx: string): boolean {
  if (nxx.length !== 3) return false;
  const d0 = nxx[0]!;
  if (d0 < '2' || d0 > '9') return false;
  if (isNanpN11Reserved(nxx)) return false;
  if (nxx === '555' || nxx === '000' || nxx === '911') return false;
  return true;
}

export function normalizeEmail(email: string): string {
  return email.trim().toLowerCase();
}

export function validateFullName(name: string, touched: boolean): string {
  if (!touched) return '';
  const t = name.trim();
  if (!t) return R.fullNameRequired;
  if (t.length > FIELD_LIMITS.fullName) return R.fullNameInvalid;
  if (hasEmoji(t)) return R.fullNameInvalid;
  if (!NAME_CHARS_RE.test(t)) return R.fullNameInvalid;
  return '';
}

export function validateEmailField(email: string, touched: boolean): string {
  if (!touched) return '';
  const t = email.trim();
  if (!t) return R.emailRequired;
  if (t.length > FIELD_LIMITS.email) return R.emailInvalid;
  if (!EMAIL_RE.test(normalizeEmail(email))) return R.emailInvalid;
  return '';
}

export function validatePasswordField(password: string, touched: boolean): string {
  if (!touched) return '';
  if (!password || !password.trim()) return R.passwordRequired;
  if (password.length > 128) return R.passwordRules;
  if (password.length < 8) return R.passwordRules;
  if (!/[a-z]/.test(password)) return R.passwordRules;
  if (!/[A-Z]/.test(password)) return R.passwordRules;
  if (!/\d/.test(password)) return R.passwordRules;
  if (!PASSWORD_SPECIAL_RE.test(password)) return R.passwordRules;
  return '';
}

export function validateConfirmPassword(
  confirmPassword: string,
  password: string,
  touched: boolean
): string {
  if (!touched) return '';
  if (!confirmPassword.trim()) return R.confirmRequired;
  if (confirmPassword.trim() !== password.trim()) return R.confirmMismatch;
  return '';
}

export function validatePhoneUS(phone: string, touched: boolean): string {
  if (!touched) return '';
  const digits = phone.replace(/\D/g, '');
  if (digits.length !== 10) return R.phoneInvalid;
  if (REPEATED_DIGITS.test(digits)) return R.phoneInvalid;
  if (KNOWN_FAKE_PHONES.has(digits)) return R.phoneInvalid;
  const areaCode = digits.substring(0, 3);
  const exchangeCode = digits.substring(3, 6);
  if (!isValidNanpAreaCode(areaCode) || !isValidNanpExchange(exchangeCode)) {
    return R.phoneInvalid;
  }
  return '';
}

export function validateStreet(address: string, touched: boolean): string {
  if (!touched) return '';
  const t = address.trim();
  if (!t) return R.streetRequired;
  if (t.length > FIELD_LIMITS.street) return R.streetInvalid;
  if (hasEmoji(t)) return R.streetInvalid;
  if (!STREET_CHARS_RE.test(t)) return R.streetInvalid;
  return '';
}

export function validateCityField(city: string, touched: boolean): string {
  if (!touched) return '';
  const t = city.trim();
  if (!t) return R.cityRequired;
  if (t.length > FIELD_LIMITS.city) return R.cityInvalid;
  if (hasEmoji(t)) return R.cityInvalid;
  if (!CITY_CHARS_RE.test(t)) return R.cityInvalid;
  return '';
}

export function validateStateField(state: string, touched: boolean): string {
  if (!touched) return '';
  const t = state.trim();
  if (!t) return R.stateRequired;
  if (!VALID_STATE_CODES.has(t)) return R.stateRequired;
  return '';
}

export function validateZipField(zipCode: string, touched: boolean): string {
  if (!touched) return '';
  const raw = zipCode.trim();
  if (!raw) return R.zipInvalid;
  const digits = raw.replace(/\D/g, '');
  if (digits.length === 5 || digits.length === 9) return '';
  return R.zipInvalid;
}

export function validateSalonNameField(salonName: string, touched: boolean, isStylist: boolean): string {
  if (!isStylist || !touched) return '';
  const t = salonName.trim();
  if (!t) return R.salonRequired;
  if (t.length > FIELD_LIMITS.salonName) return R.salonInvalid;
  if (hasEmoji(t)) return R.salonInvalid;
  if (!SALON_CHARS_RE.test(t)) return R.salonInvalid;
  return '';
}

export function validateSpecialtiesField(specialtyIds: string[], touched: boolean, isStylist: boolean): string {
  if (!isStylist || !touched) return '';
  if (!specialtyIds || specialtyIds.length === 0) return R.specialtyRequired;
  return '';
}

/** When mobile services are enabled, validates miles (1–100). Use touched=true on final submit. */
export function validateTravelDistanceMiles(
  travelDistance: number,
  providesMobileService: boolean,
  touched: boolean
): string {
  if (!providesMobileService || !touched) return '';
  const d = travelDistance;
  if (!d || d < 1) return R.travelDistanceRequired;
  if (d > 100) return R.travelDistanceMax;
  return '';
}

export function validateUnitField(unit: string | undefined): string {
  if (!unit || !unit.trim()) return '';
  if (unit.length > FIELD_LIMITS.unit) return R.unitTooLong;
  if (hasEmoji(unit)) return R.streetInvalid;
  return '';
}
